import "react-toastify/dist/ReactToastify.css";
import {Form, NavLink, useLocation, useNavigate} from "react-router-dom";
import Select from "react-select";
import React, {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {gql, useMutation} from "@apollo/client";
import {Autocomplete, useLoadScript} from "@react-google-maps/api";
import Loading from "../../components/common/Loading";
import {SPORTS} from "../../constants/vars";
import {useBranding} from "../../context/BrandingContext";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import BrandName from "../../components/common/BrandName";

// --- GraphQL mutation ---
// Graphene will expose snake_case args in camelCase.
const REGISTRATION_MUTATION = gql`
    mutation WebRegistration(
        $accountType: String!
        $email: String!
        $password1: String!
        $password2: String!
        $mobile: String!
        $firstName: String!
        $lastName: String!
        $dob: Date
        $gender: String
        $organizationType: String
        $organization: String
        $location: LocationInput
        $sponsorName: String
        $contactName: String
        $billingEmail: String
        $websiteUrl: String
        $primarySport: String
    ) {
        webRegistration(
            accountType: $accountType
            email: $email
            password1: $password1
            password2: $password2
            mobile: $mobile
            firstName: $firstName
            lastName: $lastName
            dob: $dob
            gender: $gender
            organizationType: $organizationType
            organization: $organization
            location: $location
            sponsorName: $sponsorName
            contactName: $contactName
            billingEmail: $billingEmail
            websiteUrl: $websiteUrl
            primarySport: $primarySport
        ) {
            success
            errors
        }
    }
`;

function Register() {
    const {branding, theme, isOrgPortal, ready} = useBranding();
    const colors = useThemeColors();
    const {t} = useTranslation();

    // ----- OPTIONS -----
    const orgTypeOptions = [
        {value: "league", label: "League / Tournament"},
        {value: "academy", label: "Academy"},
    ];

    const inputStyle = {
        backgroundColor: colors.surface,
        color: colors.textOnSurface,
        border: `1px solid ${colors.primary}`,
        borderRadius: "8px",
        padding: "12px",
        fontSize: "16px",
        width: "100%",
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.primary}`,
            borderRadius: "8px",
            padding: "5px",
            color: colors.textOnPrimary,
            fontSize: "16px",
            borderColor: state.isFocused ? colors.primary : colors.primary,
            boxShadow: "none",
            "&:hover": {
                borderColor: colors.primary,
            },
        }),
        option: (base) => ({
            ...base,
            color: colors.primary,
            cursor: "pointer",
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: colors.surface,
            color: colors.textOnSurface,
            border: `1px solid ${colors.primary}`,
            borderRadius: "8px",
            overflow: "hidden",
        }),
        singleValue: (base) => ({
            ...base,
            color: colors.textOnSurface,
        }),
        placeholder: (base) => ({
            ...base,
            color: colors.textOnSurface,
        }),
        input: (base) => ({
            ...base,
            color: colors.textOnSurface,
            borderColor: colors.primary,
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: colors.textOnSurface,
        }),
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: colors.border,
        }),
    };

    const sportOptions = SPORTS.map((sport) => ({
        value: sport.key,
        key: sport.key,
        label: t(`signUp.form.sports.${sport.key}`),
    }));
    const genderOptions = [
        {value: "", label: t("signUp.form.genderNotSpecified") || "Prefer not to say"},
        {value: "male", label: t("signUp.form.genderMale") || "Male"},
        {value: "female", label: t("signUp.form.genderFemale") || "Female"},
    ];

    // ----- REFS / STATE -----
    const firstName = useRef();
    const lastName = useRef();
    const email = useRef();
    const mobile = useRef();
    const password = useRef();
    const password1 = useRef();
    const dob = useRef();
    const [gender, setGender] = useState(null);

    // Default account type is player.
    const [accountType, setAccountType] = useState("player");

    // Manager fields
    const [organizationType, setOrganizationType] = useState(null);
    const organization = useRef();

    // Sponsor fields
    const [sponsorName, setSponsorName] = useState("");
    const [contactName, setContactName] = useState("");
    const [billingEmail, setBillingEmail] = useState("");
    const [websiteUrl, setWebsiteUrl] = useState("");
    const [primarySport, setPrimarySport] = useState(null);

    // Location for all account types
    const [location, setLocation] = useState({
        address: "",
        city: "",
        state: "",
        country: "",
        countryCode: "",
        latitude: null,
        longitude: null,
        placeId: "",
    });

    const [searchResult, setSearchResult] = useState(null);

    const [errors, setErrors] = useState([]);
    const registrationInFlightRef = useRef(false);
    const [registerWeb, {loading}] = useMutation(REGISTRATION_MUTATION);

    const navigate = useNavigate();
    const locationHook = useLocation();

    const accountTypeFromState = locationHook.state?.accountType;

    useEffect(() => {
        if (
            accountTypeFromState === "sponsor" ||
            accountTypeFromState === "manager" ||
            accountTypeFromState === "player"
        ) {
            setAccountType(accountTypeFromState);
        }
    }, [accountTypeFromState]);

    const {isLoaded} = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: ["places"],
    });

    if (!ready) {
        return null;
    }

    const isSponsor = accountType === "sponsor";
    const isManager = accountType === "manager";
    const showBusinessInfo = isManager || isSponsor;

    const orgName = isOrgPortal
        ? branding?.organization?.name || "Organization"
        : "AngeonX Sports";

    const logoSrc = isOrgPortal
        ? branding?.logoUrl ||
        branding?.organization?.logoUrl ||
        theme.logo.short
        : "/dist/img/angeonx-sports.png";

    function onLoad(autocomplete) {
        setSearchResult(autocomplete);
    }

    function onPlaceChanged() {
        if (!searchResult) {
            alert("Please enter text");
            return;
        }

        const place = searchResult.getPlace();

        const locObj = {
            address: place.formatted_address || "",
            city: "",
            state: "",
            country: "",
            countryCode: "",
            latitude: place.geometry?.location?.lat() ?? null,
            longitude: place.geometry?.location?.lng() ?? null,
            placeId: place.place_id || "",
        };

        place.address_components?.forEach((component) => {
            const types = component.types || [];

            if (types.includes("locality")) {
                locObj.city = component.long_name;
            }

            if (types.includes("administrative_area_level_1")) {
                locObj.state = component.long_name;
            }

            if (types.includes("country")) {
                locObj.country = component.long_name;
                locObj.countryCode = component.short_name;
            }
        });

        setLocation(locObj);
    }

    const parseErrors = (errs) => {
        let errList = errs;

        try {
            if (
                Array.isArray(errList) &&
                typeof errList[0] === "string" &&
                errList[0].startsWith("[")
            ) {
                errList = JSON.parse(errList[0]);
            }
        } catch (e) {
            console.error("Error parsing error messages:", e);
        }

        return Object.values(errList)
            .flat()
            .map((err) => (typeof err === "string" ? err : err.message))
            .flatMap((msg) => msg.split(/\. ?/))
            .map((msg) => msg.trim())
            .filter((msg) => msg.length > 0);
    };

    const resetRoleSpecificErrorsAndData = (nextAccountType) => {
        setAccountType(nextAccountType);
        setErrors([]);

        // Keep location because all account types need it.
        // Clear only manager/sponsor-specific data when switching to player.
        if (nextAccountType === "player") {
            setOrganizationType(null);
            setSponsorName("");
            setContactName("");
            setBillingEmail("");
            setWebsiteUrl("");
            setPrimarySport(null);
        }
    };

    const handleSubmit = () => {
        if (loading || registrationInFlightRef.current) return;

        const tmpErrors = [];

        if (!firstName.current.value) {
            tmpErrors.push({message: "First name is required"});
        }

        if (!lastName.current.value) {
            tmpErrors.push({message: "Last name is required"});
        }

        if (!email.current.value) {
            tmpErrors.push({message: "Email is required"});
        }

        if (!mobile.current.value) {
            tmpErrors.push({message: "Mobile is required"});
        }

        if (!password.current.value || !password1.current.value) {
            tmpErrors.push({message: "Password is required"});
        }

        if (password.current.value !== password1.current.value) {
            tmpErrors.push({message: "Passwords do not match"});
        }

        if (!accountType) {
            tmpErrors.push({message: "Please choose account type"});
        }

        if (!dob.current.value) {
            tmpErrors.push({message: "Date of birth is required"});
        }

        if (isManager) {
            if (!organizationType) {
                tmpErrors.push({message: "Organization type is required"});
            }

            if (!organization.current?.value) {
                tmpErrors.push({message: "Organization name is required"});
            }
        }

        if (isSponsor) {
            if (!sponsorName) {
                tmpErrors.push({
                    message: "Business / Sponsor name is required",
                });
            }

            if (!billingEmail) {
                tmpErrors.push({message: "Billing email is required"});
            }
        }

        // Location is required for all account types.
        if (!location.address) {
            tmpErrors.push({message: "Location is required"});
        }

        if (tmpErrors.length > 0) {
            setErrors(tmpErrors);
            return;
        }

        setErrors([]);
        registrationInFlightRef.current = true;

        const finalVars = {
            accountType,
            firstName: firstName.current.value,
            lastName: lastName.current.value,
            dob: dob.current.value,
            gender: gender?.value || null,
            email: email.current.value,
            mobile: mobile.current.value,
            password1: password.current.value,
            password2: password1.current.value,
            location: {
                address: location.address,
                city: location.city,
                state: location.state,
                country: location.country,
                countryCode: location.countryCode,
                latitude: location.latitude,
                longitude: location.longitude,
                placeId: location.placeId,
            },
        };

        if (isManager) {
            finalVars.organizationType = organizationType?.value || null;
            finalVars.organization = organization.current.value;
        }

        if (isSponsor) {
            finalVars.sponsorName = sponsorName;
            finalVars.contactName = contactName;
            finalVars.billingEmail = billingEmail;
            finalVars.websiteUrl = websiteUrl;
            finalVars.primarySport = primarySport?.value || null;
        }

        registerWeb({
            variables: finalVars,
        })
            .then(({data}) => {
                if (data.webRegistration.success) {
                    toast.success(t("signUp.toast.created"), {
                        autoClose: false,
                        toastId: "web_register_success",
                        position: "top-center",
                        closeOnClick: true,
                    });

                    navigate("/login", {replace: true});
                } else {
                    setErrors(parseErrors(data.webRegistration.errors));
                }
            })
            .catch(() => {
                toast.error(t("signUp.toast.error"), {
                    autoClose: 3000,
                    toastId: "web_register_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            })
            .finally(() => {
                registrationInFlightRef.current = false;
            });
    };

    if (!isLoaded) {
        return <Loading/>;
    }

    const headingText = isManager
        ? t("signUp.titleManager")
        : isSponsor
            ? t("signUp.addSponsor")
            : t("signUp.addAccount");

    const roleOptions = [
        {
            key: "player",
            icon: "fas fa-user",
            title: t("signUp.form.player"),
            description:
                t("signUp.form.playerDescription") ||
                "Join teams, manage your profile, and participate in events.",
        },
        {
            key: "manager",
            icon: "fas fa-users-cog",
            title: t("signUp.form.manager"),
            description:
                t("signUp.form.managerDescription") ||
                "Create leagues, academies, teams, schedules, and manage events.",
        },
        {
            key: "sponsor",
            icon: "fas fa-bullhorn",
            title: t("signUp.form.sponsor"),
            description:
                t("signUp.form.sponsorDescription") ||
                "Promote your business to sports communities and local audiences.",
        },
    ];

    return (
        <div
            style={{
                backgroundColor: colors.background,
                color: colors.textOnBackground,
                fontFamily: "'Poppins', sans-serif",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
                overflowX: "hidden",
            }}
        >
            <div className="col-12 col-md-8 col-lg-6">
                <div
                    style={{
                        backgroundColor: colors.surface,
                        borderRadius: "12px",
                        padding: "30px",
                        boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.3)",
                        width: "100%",
                    }}
                >
                    {/* Logo / Brand */}
                    <div className="text-center">
                        <NavLink to="/">
                            <img
                                src={logoSrc}
                                alt={orgName}
                                className="logo img-fluid"
                                style={{
                                    height: isOrgPortal ? "80px" : "112px",
                                    marginBottom: isOrgPortal ? "10px" : 0,
                                }}
                            />
                        </NavLink>

                        {!isOrgPortal && (
                            <div
                                style={{
                                    color: colors.primary,
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: "24px",
                                    fontWeight: 800,
                                    letterSpacing: "0.1px",
                                    lineHeight: 1.1,
                                    marginTop: "6px",
                                }}
                            >
                                <BrandName
                                    size="inherit"
                                    sportsStyle={{marginLeft: 3}}
                                />
                            </div>
                        )}

                        {isOrgPortal && (
                            <h2
                                className="brand-text"
                                style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: colors.primary,
                                }}
                            >
                                <>
                                    {orgName}
                                    <span
                                        style={{
                                            display: "block",
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: colors.textOnSurface,
                                            marginTop: 2,
                                        }}
                                    >
                                        Powered by {" "}
                                        <BrandName size="inherit" sportsStyle={{marginLeft: 2}} />
                                    </span>
                                </>
                            </h2>
                        )}
                    </div>

                    <div className="text-center mb-3">
                        <p
                            className="login-box-msg"
                            style={{color: colors.textOnSurface}}
                        >
                            {headingText}
                        </p>
                    </div>

                    {/* ERROR BOX */}
                    {errors.length > 0 && (
                        <div className="alert alert-danger alert-dismissible">
                            <button
                                type="button"
                                className="close"
                                data-dismiss="alert"
                                aria-hidden="true"
                                style={{color: "#fff"}}
                                onClick={() => setErrors([])}
                            >
                                &times;
                            </button>

                            <h5
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: colors.primary,
                                }}
                            >
                                <i className="icon fas fa-ban"></i>{" "}
                                Registration Errors
                            </h5>

                            <ul
                                style={{
                                    paddingLeft: "20px",
                                    margin: "10px 0",
                                }}
                            >
                                {parseErrors(errors).map((msg, index) => (
                                    <li
                                        key={index}
                                        style={{
                                            listStyleType: "disc",
                                            marginBottom: "5px",
                                            fontSize: "16px",
                                        }}
                                    >
                                        {msg}.
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <Form
                        method="post"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                    >
                        {/* ======================== */}
                        {/* USER INFORMATION SECTION */}
                        {/* ======================== */}
                        <div className="mb-3">
                            <h5
                                style={{
                                    color: colors.primary,
                                    marginBottom: "10px",
                                }}
                            >
                                {t("signUp.yourInfo")}
                            </h5>
                        </div>

                        {/* First / Last */}
                        <div className="row">
                            <div className="col-12 col-md-6 mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("signUp.form.firstName")}
                                    ref={firstName}
                                    style={inputStyle}
                                />
                            </div>

                            <div className="col-12 col-md-6 mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("signUp.form.lastName")}
                                    ref={lastName}
                                    style={inputStyle}
                                />
                            </div>
                        </div>

                        {/* DOB */}
                        {/* DOB */}
                        <div className="row">
                            <div className="col-12 mb-3">
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "10px",
                                        fontSize: "14px",
                                        fontWeight: "700",
                                        color: colors.primary,
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "10px",
                                            background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <i
                                            className="fas fa-birthday-cake"
                                            style={{
                                                color: colors.textOnPrimary,
                                                fontSize: "13px",
                                            }}
                                        />
                                    </div>

                                    <span>
                {t("signUp.form.dob") || "Date of birth"}
            </span>
                                </label>

                                <div
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: colors.surface,
                                        border: `1.5px solid ${colors.primary}`,
                                        borderRadius: "14px",
                                        overflow: "hidden",
                                        transition: "all 0.25s ease",
                                        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "54px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            alignSelf: "stretch",
                                            background: `linear-gradient(180deg, ${colors.secondary}, ${colors.primary})`,
                                            borderRight: `1px solid ${colors.primary}`,
                                        }}
                                    >
                                        <i
                                            className="fas fa-calendar-alt"
                                            style={{
                                                color: colors.textOnPrimary,
                                                fontSize: "16px",
                                            }}
                                        />
                                    </div>

                                    <input
                                        type="date"
                                        className="form-control"
                                        ref={dob}
                                        max={new Date().toISOString().split("T")[0]}
                                        style={{
                                            backgroundColor: "transparent",
                                            color: colors.textOnSurface,
                                            border: "none",
                                            boxShadow: "none",
                                            padding: "16px",
                                            fontSize: "16px",
                                            fontWeight: "500",
                                            width: "100%",
                                            minHeight: "58px",
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        marginTop: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        fontSize: "12px",
                                        color: colors.textOnSurface,
                                        opacity: 0.72,
                                        paddingLeft: "4px",
                                    }}
                                >
                                    <i
                                        className="fas fa-info-circle"
                                        style={{
                                            fontSize: "11px",
                                            color: colors.primary,
                                        }}
                                    />

                                    <span>
                {t("signUp.form.dobHint") ||
                    "Used for age eligibility, divisions, and player verification."}
            </span>
                                </div>
                            </div>
                        </div>

                        {/* Gender */}
                        <div className="row">
                            <div className="col-12 mb-3">
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "10px",
                                        fontSize: "14px",
                                        fontWeight: "700",
                                        color: colors.primary,
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "10px",
                                            background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <i
                                            className="fas fa-user"
                                            style={{
                                                color: colors.textOnPrimary,
                                                fontSize: "13px",
                                            }}
                                        />
                                    </div>

                                    <span>{t("signUp.form.gender") || "Gender"}</span>
                                </label>

                                <Select
                                    value={gender}
                                    onChange={setGender}
                                    options={genderOptions}
                                    placeholder={t("signUp.form.gender") || "Gender"}
                                    styles={selectStyles}
                                    isSearchable={false}
                                />
                            </div>
                        </div>

                        {/* Email / Mobile */}
                        <div className="row">
                            <div className="col-12 col-md-6 mb-3">
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder={t("signUp.form.email")}
                                    ref={email}
                                    style={inputStyle}
                                />
                            </div>

                            <div className="col-12 col-md-6 mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("signUp.form.mobile")}
                                    ref={mobile}
                                    style={inputStyle}
                                />
                            </div>
                        </div>

                        {/* Password / Confirm */}
                        <div className="row">
                            <div className="col-12 col-md-6 mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder={t("signUp.form.password")}
                                    ref={password}
                                    style={inputStyle}
                                />
                            </div>

                            <div className="col-12 col-md-6 mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder={t("signUp.form.password2")}
                                    ref={password1}
                                    style={inputStyle}
                                />
                            </div>
                        </div>

                        {/* ======================== */}
                        {/* ACCOUNT TYPE SELECTOR   */}
                        {/* ======================== */}
                        <div className="mb-4">
                            <h5
                                style={{
                                    color: colors.primary,
                                    marginBottom: "12px",
                                }}
                            >
                                {t("signUp.role")}
                            </h5>

                            <div
                                className="row"
                                style={{
                                    rowGap: "14px",
                                }}
                            >
                                {roleOptions.map((role) => {
                                    const isActive = accountType === role.key;

                                    return (
                                        <div
                                            className="col-12 col-md-4"
                                            key={role.key}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    resetRoleSpecificErrorsAndData(
                                                        role.key
                                                    )
                                                }
                                                style={{
                                                    width: "100%",
                                                    height: "190px",
                                                    background: isActive
                                                        ? `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`
                                                        : colors.background,
                                                    border: isActive
                                                        ? `2px solid ${colors.primary}`
                                                        : `1px solid ${
                                                            colors.border ||
                                                            colors.primary
                                                        }`,
                                                    borderRadius: "16px",
                                                    padding: "16px 14px",
                                                    cursor: "pointer",
                                                    textAlign: "left",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent:
                                                        "space-between",
                                                    gap: "10px",
                                                    boxShadow: isActive
                                                        ? "0 12px 28px rgba(0, 0, 0, 0.28)"
                                                        : "0 6px 16px rgba(0, 0, 0, 0.16)",
                                                    transform: isActive
                                                        ? "translateY(-2px)"
                                                        : "translateY(0)",
                                                    transition:
                                                        "all 0.2s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.transform =
                                                            "translateY(-2px)";
                                                        e.currentTarget.style.borderColor =
                                                            colors.primary;
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.transform =
                                                            "translateY(0)";
                                                        e.currentTarget.style.borderColor =
                                                            colors.border ||
                                                            colors.primary;
                                                    }
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: "10px",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "42px",
                                                            height: "42px",
                                                            borderRadius:
                                                                "14px",
                                                            backgroundColor:
                                                                isActive
                                                                    ? "rgba(0, 0, 0, 0.18)"
                                                                    : colors.surface,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            border: `1px solid ${
                                                                isActive
                                                                    ? "rgba(0, 0, 0, 0.12)"
                                                                    : colors.primary
                                                            }`,
                                                        }}
                                                    >
                                                        <i
                                                            className={
                                                                role.icon
                                                            }
                                                            style={{
                                                                fontSize:
                                                                    "18px",
                                                                color: isActive
                                                                    ? colors.textOnSecondary
                                                                    : colors.primary,
                                                            }}
                                                        />
                                                    </div>

                                                    {isActive && (
                                                        <div
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius:
                                                                    "50%",
                                                                backgroundColor:
                                                                    "rgba(0, 0, 0, 0.2)",
                                                                display:
                                                                    "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                            }}
                                                        >
                                                            <i
                                                                className="fas fa-check"
                                                                style={{
                                                                    fontSize:
                                                                        "12px",
                                                                    color: colors.textOnSecondary,
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div>
                                                    <div
                                                        style={{
                                                            fontSize: "16px",
                                                            fontWeight: 800,
                                                            color: isActive
                                                                ? colors.textOnSecondary
                                                                : colors.textOnSurface,
                                                            marginBottom:
                                                                "6px",
                                                            minHeight: "22px",
                                                        }}
                                                    >
                                                        {role.title}
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize: "12px",
                                                            lineHeight: 1.45,
                                                            minHeight: "52px",
                                                            color: isActive
                                                                ? colors.textOnSecondary
                                                                : colors.textOnSurface,
                                                            opacity: isActive
                                                                ? 0.9
                                                                : 0.72,
                                                        }}
                                                    >
                                                        {role.description}
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ============================== */}
                        {/* ACCOUNT / BUSINESS INFORMATION */}
                        {/* Hidden when accountType is player */}
                        {/* ============================== */}
                        {showBusinessInfo && (
                            <>
                                <div className="mb-3 mt-4">
                                    <h5
                                        style={{
                                            color: colors.primary,
                                            marginBottom: "10px",
                                        }}
                                    >
                                        {isManager
                                            ? t("signUp.orgInfo")
                                            : t("signUp.sponsorInfo")}
                                    </h5>
                                </div>

                                <div className="row">
                                    {/* Manager-only fields */}
                                    {isManager && (
                                        <>
                                            <div className="col-12 col-md-6 mb-3">
                                                <Select
                                                    placeholder={t(
                                                        "signUp.form.orgType"
                                                    )}
                                                    options={orgTypeOptions}
                                                    value={organizationType}
                                                    onChange={
                                                        setOrganizationType
                                                    }
                                                    className="select-input"
                                                    styles={selectStyles}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6 mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t(
                                                        "signUp.form.orgName"
                                                    )}
                                                    ref={organization}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Sponsor-only fields */}
                                    {isSponsor && (
                                        <>
                                            <div className="col-12 col-md-6 mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t(
                                                        "signUp.form.sponsorName"
                                                    )}
                                                    value={sponsorName}
                                                    onChange={(e) =>
                                                        setSponsorName(
                                                            e.target.value
                                                        )
                                                    }
                                                    style={inputStyle}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6 mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t(
                                                        "signUp.form.website"
                                                    )}
                                                    value={websiteUrl}
                                                    onChange={(e) =>
                                                        setWebsiteUrl(
                                                            e.target.value
                                                        )
                                                    }
                                                    style={inputStyle}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6 mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t(
                                                        "signUp.form.contactName"
                                                    )}
                                                    value={contactName}
                                                    onChange={(e) =>
                                                        setContactName(
                                                            e.target.value
                                                        )
                                                    }
                                                    style={inputStyle}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6 mb-3">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder={t(
                                                        "signUp.form.contactEmail"
                                                    )}
                                                    value={billingEmail}
                                                    onChange={(e) =>
                                                        setBillingEmail(
                                                            e.target.value
                                                        )
                                                    }
                                                    style={inputStyle}
                                                />
                                            </div>

                                            <div className="col-12 col-md-12 mb-3">
                                                <Select
                                                    placeholder={t(
                                                        "signUp.form.sportAudience"
                                                    )}
                                                    options={sportOptions}
                                                    value={primarySport}
                                                    onChange={setPrimarySport}
                                                    className="select-input"
                                                    styles={selectStyles}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        )}

                        {/* ============================== */}
                        {/* LOCATION - shown for all roles */}
                        {/* ============================== */}
                        <div className="mb-3 mt-4">
                            <h5
                                style={{
                                    color: colors.primary,
                                    marginBottom: "10px",
                                }}
                            >
                                {t("signUp.form.locationInfo") ||
                                    "Location Information"}
                            </h5>
                        </div>

                        <div className="row">
                            <div className="col-12 mb-3">
                                <Autocomplete
                                    onPlaceChanged={onPlaceChanged}
                                    onLoad={onLoad}
                                >
                                    <input
                                        className="form-control"
                                        type="text"
                                        placeholder={
                                            isManager
                                                ? t("signUp.form.orgLocation")
                                                : isSponsor
                                                    ? t(
                                                        "signUp.form.sponsorLocation"
                                                    )
                                                    : t(
                                                    "signUp.form.yourLocation"
                                                ) || "Your location"
                                        }
                                        style={inputStyle}
                                    />
                                </Autocomplete>

                                <div
                                    style={{
                                        marginTop: "5px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <i
                                        className="fas fa-info-circle"
                                        title={
                                            isManager
                                                ? t(
                                                    "signUp.form.orgLocationInfo"
                                                )
                                                : isSponsor
                                                    ? t(
                                                        "signUp.form.sponsorLocationInfo"
                                                    )
                                                    : t(
                                                        "signUp.form.yourLocationInfo"
                                                    ) ||
                                                    "Your location helps us connect you with nearby teams, leagues, events, and sports opportunities."
                                        }
                                        style={{
                                            color: colors.primary,
                                            marginRight: "8px",
                                            cursor: "help",
                                        }}
                                    />

                                    <span
                                        style={{
                                            fontSize: "14px",
                                            color: colors.primary,
                                        }}
                                    >
                                        {isManager
                                            ? t(
                                                "signUp.form.orgLabelLocation"
                                            )
                                            : isSponsor
                                                ? t(
                                                    "signUp.form.sponsorLabelLocation"
                                                )
                                                : t(
                                                    "signUp.form.playerLabelLocation"
                                                ) ||
                                                "Used to connect you with nearby sports opportunities."}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Terms */}
                        <div className="text-center mt-3">
                            <label
                                htmlFor="agreeTerms"
                                className="signup-text"
                                style={{fontSize: "16px"}}
                            >
                                {t("signUp.form.agreeTo")}

                                <NavLink
                                    to="/terms"
                                    style={{
                                        color: colors.primary,
                                        fontWeight: "bold",
                                        textDecoration: "underline",
                                        fontSize: "16px",
                                        marginLeft: "5px",
                                    }}
                                >
                                    {t("signUp.form.terms")}
                                </NavLink>
                            </label>
                        </div>

                        {/* Submit */}
                        <div className="text-center mt-3">
                            <button
                                type="submit"
                                className="btn mt-3 col-12"
                                disabled={loading}
                                style={{
                                    backgroundColor: colors.primary,
                                    color: colors.textOnPrimary,
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                }}
                                onMouseOver={(e) =>
                                    (e.target.style.backgroundColor =
                                        colors.secondary)
                                }
                                onMouseOut={(e) =>
                                    (e.target.style.backgroundColor =
                                        colors.primary)
                                }
                            >
                                {loading
                                    ? t("signUp.form.creatingLabel")
                                    : t("auth.registerTitle")}
                            </button>
                        </div>
                    </Form>

                    {/* Login link */}
                    <div
                        className="text-center mt-3"
                        style={{fontSize: "16px"}}
                    >
                        <NavLink
                            to="/login"
                            style={{
                                color: colors.primary,
                                fontWeight: "bold",
                                textDecoration: "underline",
                                fontSize: "16px",
                            }}
                        >
                            {t("signUp.alreadyHaveAccount")}
                        </NavLink>
                    </div>

                    {/* Resend Activation */}
                    <div
                        className="signup-container text-center mt-3"
                        style={{
                            fontSize: "14px",
                            color: colors.primary,
                        }}
                    >
                        {t("auth.didntReceived")}{" "}

                        <NavLink
                            to="/resend-activation"
                            className="text-center d-block"
                            style={{
                                color: colors.primary,
                                fontWeight: "bold",
                                textDecoration: "underline",
                                fontSize: "14px",
                                margin: 12,
                            }}
                        >
                            {t("auth.resendActivation")}
                        </NavLink>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Register;
